import {
  EXPULSE_IMPULSE_OUT,
  EXPULSE_IMPULSE_UP,
  SIGN_ARM_SECONDS,
  SIGN_ROOT_SECONDS,
  SIGN_TRIGGER_RADIUS,
  STORM_CAST_RANGE,
  STORM_TICK_SECONDS,
  WALL_CLEARANCE,
  WALL_DISTANCE,
  WALL_HEIGHT,
} from '../constants.ts';
import { type Dice, rollDice } from '../dice.ts';
import { dist, forwardVector, type Vec3 } from '../math.ts';
import {
  type Barrier,
  BARRIER_PROFILES,
  insideBarrier,
  insideStorm,
  onSign,
  type Sign,
  WALL_SIZE,
} from '../placed.ts';
import { drainPp, isEmbodied, type PlayerState } from '../player.ts';
import type { SpellDef } from '../spells.ts';
import { dealDamage } from './death.ts';
import { applyChill } from './projectiles.ts';
import { eyePos, findPlayer, type SimEvent, type SimState, type WorldQuery } from './state.ts';

/**
 * Placed effects: walls, signs and storms — spells that put something into the
 * world rather than onto a player.
 *
 * Core owns them outright. The renderer mirrors them, and a solid wall is given
 * a collider on both ends by `net/levelColliders.ts`, but whether a spell got
 * past one is decided here, with a segment test, so the server and the client
 * reach the same verdict.
 */

/**
 * The floor the caster is looking at.
 *
 * Two rays, because one is not enough: the aim ray finds whatever is under the
 * crosshair, which may be a wall face, a pillar or the ceiling, and the second
 * drops from there to the floor. Aiming at a wall puts the storm at its foot,
 * which is what a player expects and what makes doorway denial possible.
 */
export function groundUnderAim(world: WorldQuery, from: Vec3, dir: Vec3): Vec3 | null {
  const hit = world.raycast(from, dir, STORM_CAST_RANGE);
  const landing = hit ? hit.point : {
    x: from.x + dir.x * STORM_CAST_RANGE,
    y: from.y + dir.y * STORM_CAST_RANGE,
    z: from.z + dir.z * STORM_CAST_RANGE,
  };
  const down = world.raycast(
    { x: landing.x, y: landing.y + 0.25, z: landing.z },
    { x: 0, y: -1, z: 0 },
    40,
  );
  return down ? down.point : null;
}

/**
 * A wall springs up in front of the caster, square to their facing and standing
 * on whatever floor is under it.
 *
 * Two constraints make this fiddlier than it looks. It must not spawn inside the
 * masonry the caster is facing, so the forward distance is shortened to whatever
 * is actually clear; and it must sit on the floor rather than at the caster's own
 * height, or a wall cast from a dais hangs in the air over the room below.
 */
export function placeWall(
  s: SimState, p: PlayerState, spell: SpellDef, ti: number,
  world: WorldQuery, dice: Dice | undefined,
): Barrier | null {
  const profiles = BARRIER_PROFILES[spell.id];
  if (!profiles) return null;
  const profile = profiles[Math.min(profiles.length - 1, ti)];

  const facing = forwardVector(p.yaw, 0);
  const eye = eyePos(p);
  const ahead = world.raycast(eye, facing, WALL_DISTANCE);
  const reach = ahead
    ? Math.max(0.8, ahead.distance - WALL_CLEARANCE)
    : WALL_DISTANCE;

  const centre = { x: p.pos.x + facing.x * reach, y: p.pos.y, z: p.pos.z + facing.z * reach };
  const floor = world.raycast(
    { x: centre.x, y: centre.y + 2, z: centre.z },
    { x: 0, y: -1, z: 0 },
    12,
  );
  const groundY = floor ? floor.point.y : p.pos.y;

  const size = WALL_SIZE();
  return {
    id: s.nextPlacedId++,
    ownerId: p.id,
    faction: p.faction,
    spellId: spell.id,
    remaining: spell.duration ?? 8,
    element: spell.element,
    pos: { x: centre.x, y: groundY + WALL_HEIGHT / 2, z: centre.z },
    size,
    yaw: p.yaw,
    blocksBodies: profile.blocksBodies,
    blocksSpells: profile.blocksSpells,
    passSlow: profile.passSlow,
    onPassDice: dice,
    onPassPpDrain: spell.id === 'mana_wall' ? 4 + ti * 3 : undefined,
    occupants: [],
  };
}

/** Laid at the caster's feet, on whatever floor is under them. */
export function placeSign(
  s: SimState, p: PlayerState, spell: SpellDef, world: WorldQuery, dice: Dice | undefined,
): Sign | null {
  const floor = world.raycast(
    { x: p.pos.x, y: p.pos.y + 0.5, z: p.pos.z },
    { x: 0, y: -1, z: 0 },
    6,
  );
  const groundY = floor ? floor.point.y : p.pos.y;

  return {
    id: s.nextPlacedId++,
    ownerId: p.id,
    faction: p.faction,
    spellId: spell.id,
    remaining: spell.duration ?? 45,
    element: spell.element,
    dice,
    pos: { x: p.pos.x, y: groundY, z: p.pos.z },
    triggerRadius: SIGN_TRIGGER_RADIUS,
    radius: spell.radius ?? SIGN_TRIGGER_RADIUS,
    triggersOnOwnFaction: spell.id === 'sign_expulse',
    armFor: SIGN_ARM_SECONDS,
  };
}

/**
 * Signs are one-shot. Stepping on one spends it, which is what keeps the cap of
 * three meaningful — a sign that survived its own trigger would be a permanent
 * hazard rather than a trap you have to place well.
 */
export function tickSigns(s: SimState, dt: number, out: SimEvent[]): void {
  for (let i = s.signs.length - 1; i >= 0; i--) {
    const sign = s.signs[i];
    sign.remaining -= dt;
    if (sign.armFor > 0) sign.armFor -= dt;

    if (sign.remaining <= 0) {
      s.signs.splice(i, 1);
      continue;
    }
    if (sign.armFor > 0) continue;

    const stepped = s.players.find((p) => {
      if (!isEmbodied(p)) return false;
      if (!sign.triggersOnOwnFaction && p.faction === sign.faction) return false;
      return onSign(sign, p.pos);
    });
    if (!stepped) continue;

    triggerSign(s, sign, stepped, out);
    s.signs.splice(i, 1);
  }
}

export function triggerSign(s: SimState, sign: Sign, tripped: PlayerState, out: SimEvent[]): void {
  const owner = findPlayer(s, sign.ownerId) ?? null;

  switch (sign.spellId) {
    case 'sign_explode': {
      if (!sign.dice) break;
      const roll = rollDice(s.rng, sign.dice);
      for (const target of s.players) {
        if (!isEmbodied(target)) continue;
        if (target.faction === sign.faction) continue;
        const d = dist(sign.pos, target.pos);
        if (d > sign.radius) continue;
        const scale = Math.max(0.33, 1 - d / sign.radius);
        dealDamage(s, owner, target, Math.round(roll * scale), sign.element, out);
      }
      break;
    }

    case 'sign_unpower':
      // "Empties the power of whoever steps on it" — not a drain, a floor.
      drainPp(tripped, tripped.ppMax);
      break;

    case 'sign_hesitate':
      tripped.effects.push({
        spellId: sign.spellId,
        slow: 0,
        remaining: SIGN_ROOT_SECONDS,
        total: SIGN_ROOT_SECONDS,
        sourceId: sign.ownerId,
      });
      break;

    case 'sign_expulse': {
      // Straight up from the centre, thrown if caught off it. Core sets the
      // impulse; the controller is what actually integrates the arc.
      const dx = tripped.pos.x - sign.pos.x;
      const dz = tripped.pos.z - sign.pos.z;
      const offset = Math.hypot(dx, dz);
      const outward = offset > 0.05 ? EXPULSE_IMPULSE_OUT / offset : 0;
      tripped.pendingImpulse = {
        x: dx * outward,
        y: EXPULSE_IMPULSE_UP,
        z: dz * outward,
      };
      break;
    }
  }
}

/**
 * Expiry, and the pass-through effects.
 *
 * Those fire on the transition into a wall rather than on an interval. A slab is
 * 0.6m thick and a running player is inside it for about a seventh of a second,
 * so any interval tick coarse enough to be worth rolling dice for would miss
 * almost everyone who crossed.
 */
export function tickBarriers(s: SimState, dt: number, out: SimEvent[]): void {
  for (let i = s.barriers.length - 1; i >= 0; i--) {
    const b = s.barriers[i];
    b.remaining -= dt;

    const stillInside: string[] = [];
    for (const p of s.players) {
      if (!isEmbodied(p)) continue;
      if (!insideBarrier(b, p.pos)) continue;
      stillInside.push(p.id);
      if (b.occupants.includes(p.id)) continue;

      // Newly across the threshold.
      const owner = findPlayer(s, b.ownerId) ?? null;
      // Faction checked here, not left to `dealDamage`: that rule needs an owner
      // to compare against, and a wall can outlive the mage who raised it.
      if (b.onPassDice && p.faction !== b.faction) {
        dealDamage(s, owner, p, rollDice(s.rng, b.onPassDice), b.element, out);
      }
      if (b.onPassPpDrain && p.faction !== b.faction) {
        drainPp(p, b.onPassPpDrain);
      }
    }
    b.occupants = stillInside;

    if (b.remaining <= 0) s.barriers.splice(i, 1);
  }
}

/**
 * Storms roll once per tick and apply that one roll to everyone caught, the same
 * way a blast does. Rolling per target would mean a storm that lands softly on
 * one player and hard on the player beside them, for no reason anyone can see.
 */
export function tickStorms(s: SimState, dt: number, out: SimEvent[]): void {
  for (let i = s.storms.length - 1; i >= 0; i--) {
    const storm = s.storms[i];
    storm.remaining -= dt;
    storm.nextTick -= dt;

    if (storm.nextTick <= 0) {
      storm.nextTick += STORM_TICK_SECONDS;
      const owner = findPlayer(s, storm.ownerId) ?? null;
      const roll = rollDice(s.rng, storm.dice);
      for (const target of s.players) {
        if (!isEmbodied(target)) continue;
        if (target.faction === storm.faction) continue;
        if (!insideStorm(storm, target.pos)) continue;
        dealDamage(s, owner, target, roll, storm.element, out);

        /*
         * A storm chills for as long as somebody stands in it.
         *
         * Applied per tick rather than once on entry, and refreshed rather than
         * stacked, which is what makes an Ice Storm an area a mage has to leave
         * rather than one they can walk through and shake off. Fire's storm
         * already works this way through burning; this is cold's equivalent, and
         * the two now cost the same to understand.
         */
        if (storm.chill) {
          applyChill(target, storm.chill, storm.chillSeconds ?? 1.5, storm.ownerId);
        }
      }
    }

    if (storm.remaining <= 0) s.storms.splice(i, 1);
  }
}

/**
 * Removes every wall, sign and storm a mage put into the world. For a mage who
 * has left the match: nothing they placed should outlive their leaving.
 */
export function despawnOwnedBy(s: SimState, ownerId: string): void {
  s.barriers = s.barriers.filter((b) => b.ownerId !== ownerId);
  s.signs = s.signs.filter((g) => g.ownerId !== ownerId);
  s.storms = s.storms.filter((m) => m.ownerId !== ownerId);
}
